import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ArrowDownToLine, Gamepad2 } from 'lucide-react';
import {
  getUserGameWallets,
  paymentService,
  type GameWalletItem,
} from '@/features/finance/services/paymentService';
import { dispatchBalanceRefresh } from '@/utils/balanceRefresh';
import { isGameWithdrawLanded } from '@/features/finance/utils/gameWithdrawStatus';
import { resolveGameWalletErrorMessage } from '@/utils/gameWalletErrorMessages';
import { useTranslation } from 'react-i18next';
import {
  getDisplayIntegerDollars,
  getMaxGameWithdrawAmount,
  isRiverPayProviderCode,
} from '@/utils/amountStep';
import { getIconPathForProvider, hasIconForProvider } from '@/features/games/data/gameProviders';
import { Alert, AmountInput, Button, Dialog, List, Skeleton } from '@/components/ui';
import { useModalMotion } from '@/hooks/useModalMotion';
import { ANIMATION_REGISTRY } from '@/config/animationRegistry';

type Phase = 'edit' | 'processing' | 'done';

/** Placeholder rows while the game wallets load. */
const WITHDRAW_SKELETON_ROWS = 4;

/** One game row: icon + name/availability, then amount + Max (stacked on phones, one line from sm up). */
const rowClass =
  'grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-3 gap-y-2 px-3.5 py-3 @bp-sm:grid-cols-[auto_minmax(0,1fr)_10rem_auto]';

function WithdrawGameRowSkeleton() {
  return (
    <div className={rowClass} aria-hidden>
      <Skeleton className="size-10" />
      <div className="col-span-2 flex flex-col gap-1.5 @bp-sm:col-span-1">
        <Skeleton shape="line" className="w-1/2 max-w-36" />
        <Skeleton shape="line" className="h-3 w-20" />
      </div>
      <Skeleton className="col-span-2 h-13 @bp-sm:col-span-1" />
      <Skeleton className="h-13 w-16" />
    </div>
  );
}

/** Game icon tile; a neutral game glyph when the provider has no icon. */
function GameIcon({ code }: { code?: string }) {
  return (
    <span className="inline-flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-control border border-edge-subtle bg-inset text-tertiary">
      {hasIconForProvider(code) ? (
        <img src={getIconPathForProvider(code || '')} alt="" className="block size-full object-cover" draggable={false} />
      ) : (
        <Gamepad2 className="size-5" aria-hidden />
      )}
    </span>
  );
}

export interface GameWithdrawModalProps {
  open: boolean;
  onClose: () => void;
  focusProviderId?: string | null;
  /**
   * At least one game → wallet move landed or was accepted as a request. `moved` is the total that
   * is in My money now; `requested` the total our team still has to approve (not in My money yet).
   */
  onSuccess?: (moved: number, requested: number) => void;
}

function getBalance(item: GameWalletItem): number {
  const loose = item as unknown as {
    walletBalance?: number;
    balance?: { inGameBalance?: number } | number;
    inGameBalance?: number;
  };
  const raw =
    loose.walletBalance ??
    (typeof loose.balance === 'number'
      ? loose.balance
      : loose.balance?.inGameBalance) ??
    loose.inGameBalance ??
    0;
  return Number(raw) || 0;
}

function getWinningBalance(item: GameWalletItem): number {
  const loose = item as unknown as {
    winningBalance?: number;
    balance?: { winningBalance?: number };
  };
  const raw = loose.balance?.winningBalance ?? loose.winningBalance ?? 0;
  return Number(raw) || 0;
}

function getProviderId(item: GameWalletItem): string {
  return String(
    (item as { gameProviderId?: string }).gameProviderId ??
      item.game?.id ??
      (item as { gameProvider?: { id?: string } }).gameProvider?.id ??
      (item as { id?: string }).id ??
      ''
  );
}

function getName(item: GameWalletItem): string {
  return item.game?.name || (item as { gameProvider?: { name?: string } }).gameProvider?.name || 'Game';
}

function getCode(item: GameWalletItem): string | undefined {
  return item.game?.code || (item as { gameProvider?: { code?: string } }).gameProvider?.code;
}

function getWithdrawMax(item: GameWalletItem): number {
  return getMaxGameWithdrawAmount(getCode(item), getBalance(item), getWinningBalance(item));
}

/**
 * Take money out (game → My money; the game sheet's "Take money out", also on the wallet tools page):
 * help text · per-game rows (icon, amount, Max) · footer CTA; processing and done states replace the rows.
 */
export default function GameWithdrawModal({
  open,
  onClose,
  focusProviderId,
  onSuccess,
}: GameWithdrawModalProps) {
  const { t } = useTranslation();
  const [items, setItems] = useState<GameWalletItem[]>([]);
  const [loading, setLoading] = useState(false);
  /** True after the current open/focus fetch settles — avoids empty-state flash mid-load. */
  const [listReady, setListReady] = useState(false);
  const [amounts, setAmounts] = useState<Record<string, number>>({});
  const [focusedId, setFocusedId] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [phase, setPhase] = useState<Phase>('edit');
  const [error, setError] = useState<string | null>(null);
  /** Since it opened: money now in My money, and moves our team still has to approve (kept after a partial failure). */
  const [outcome, setOutcome] = useState({ moved: 0, requested: 0 });
  /** User dismissed while withdraw requests still in flight — ignore late UI updates. */
  const dismissedRef = useRef(false);

  /** Close button, Escape, Cancel/Close/Done: stop any in-flight UI updates first. */
  const handleClose = () => {
    dismissedRef.current = true;
    onClose();
  };

  // Content stays mounted through the Dialog's exit motion; state is cleared once it is gone.
  const { render } = useModalMotion(open);

  /* Reset list UI before paint so stale rows never flash ahead of skeleton. */
  useLayoutEffect(() => {
    if (!open) return;
    dismissedRef.current = false;
    setPhase('edit');
    setError(null);
    setOutcome({ moved: 0, requested: 0 });
    setFocusedId(null);
    setDrafts({});
    setItems([]);
    setAmounts({});
    setListReady(false);
    // Keep list empty during enter — skeleton shimmer during fade causes open jank.
    setLoading(false);
  }, [open, focusProviderId]);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    // Defer fetch until enter motion finishes so skeleton/network don't fight the fade.
    const startId = window.setTimeout(() => {
      if (cancelled || dismissedRef.current) return;
      setLoading(true);
      getUserGameWallets()
        .then((res) => {
          if (cancelled || dismissedRef.current) return;
          const list =
            (res.data as { items?: GameWalletItem[] })?.items ||
            (res.data as { wallets?: GameWalletItem[] })?.wallets ||
            res.data ||
            [];
          const arr = Array.isArray(list) ? list : [];
          setItems(arr);
          const init: Record<string, number> = {};
          arr.forEach((item) => {
            const id = getProviderId(item);
            if (!id) return;
            // Focused game: prefill Max withdrawable (River Pay = winning only).
            init[id] =
              focusProviderId && id === focusProviderId ? getWithdrawMax(item) : 0;
          });
          setAmounts(init);
        })
        .catch(() => {
          if (!cancelled && !dismissedRef.current) setError(t('finance.gameWithdraw.loadFailed'));
        })
        .finally(() => {
          if (cancelled || dismissedRef.current) return;
          setListReady(true);
          setLoading(false);
        });
    }, ANIMATION_REGISTRY.modal.enterMs);
    return () => {
      cancelled = true;
      window.clearTimeout(startId);
    };
  }, [open, focusProviderId]);

  // Clear only after exit unmount — wiping mid-fade causes exit jank.
  useEffect(() => {
    if (open || render) return;
    setItems([]);
    setAmounts({});
    setListReady(false);
    setLoading(false);
    setPhase('edit');
    setError(null);
    setOutcome({ moved: 0, requested: 0 });
    setFocusedId(null);
    setDrafts({});
  }, [open, render]);

  const total = useMemo(
    () => Object.values(amounts).reduce((s, n) => s + (Number(n) || 0), 0),
    [amounts]
  );

  const setAmt = (id: string, value: number, max: number) => {
    setAmounts((prev) => ({ ...prev, [id]: Math.min(max, Math.max(0, value)) }));
  };

  const submit = async () => {
    const entries = Object.entries(amounts).filter(([, amt]) => amt > 0);
    if (!entries.length) {
      setError(t('finance.gameWithdraw.amountRequired'));
      return;
    }
    dismissedRef.current = false;
    setPhase('processing');
    setError(null);
    const errors: string[] = [];
    const successfulIds: string[] = [];
    let moved = 0;
    let requested = 0;
    for (const [gameProviderId, amount] of entries) {
      if (dismissedRef.current) return;
      try {
        const res = await paymentService.withdrawFromGame({
          gameProviderId,
          amount,
          description: 'Move from game to wallet',
        });
        if (res.success) {
          // Landed or only requested (staff approve it first): either way it must not be sent again.
          successfulIds.push(gameProviderId);
          if (isGameWithdrawLanded(res)) moved += amount;
          else requested += amount;
        } else {
          errors.push(res.message || gameProviderId);
        }
      } catch (e: unknown) {
        errors.push(
          resolveGameWalletErrorMessage(e, t, (e as { message?: string })?.message || gameProviderId, 'withdraw')
        );
      }
    }
    if (dismissedRef.current) return;

    // Refresh parent only when at least one withdraw landed or was requested (not on total failure).
    if (successfulIds.length > 0) {
      setOutcome((prev) => ({ moved: prev.moved + moved, requested: prev.requested + requested }));
      dispatchBalanceRefresh();
      onSuccess?.(moved, requested);
    }

    if (errors.length) {
      // Drop successful amounts so retry cannot double-withdraw those providers.
      if (successfulIds.length > 0) {
        setAmounts((prev) => {
          const next = { ...prev };
          for (const id of successfulIds) next[id] = 0;
          return next;
        });
        setDrafts((prev) => {
          const next = { ...prev };
          for (const id of successfulIds) delete next[id];
          return next;
        });
      }
      setError(errors.join('; '));
      setPhase('edit');
      return;
    }

    setPhase('done');
  };


  const fmt = (n: number) =>
    `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const showRows = phase === 'edit' && listReady && !loading;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      size="md"
      closeLabel={t('common.close')}
      title={t('finance.gameWithdraw.title')}
      description={t('finance.gameWithdraw.description')}
      footer={
        phase === 'edit' ? (
          <>
            <Button variant="secondary" size="lg" onClick={handleClose}>
              {t('common.cancel')}
            </Button>
            <Button variant="payout" size="lg" icon={<ArrowDownToLine />} disabled={total <= 0 || loading} onClick={submit}>
              {total > 0
                ? t('finance.gameWithdraw.submit', { amount: fmt(total) })
                : t('finance.gameWithdraw.submitEmpty')}
            </Button>
          </>
        ) : (
          <Button variant="secondary" size="lg" onClick={handleClose}>
            {phase === 'done' ? t('finance.gameWithdraw.done') : t('common.close')}
          </Button>
        )
      }
    >
      {/* Reserved height so the panel does not jump while the list loads. */}
      <div className="flex min-h-76 flex-col gap-4">
        {error ? <Alert tone="danger">{error}</Alert> : null}

        {loading ? (
          <List inset>
            {Array.from({ length: WITHDRAW_SKELETON_ROWS }, (_, i) => (
              <WithdrawGameRowSkeleton key={`wd-skel-${i}`} />
            ))}
          </List>
        ) : null}

        {phase === 'processing' ? (
          <Alert tone="info" title={t('finance.gameWithdraw.processingTitle')}>
            {t('finance.gameWithdraw.processingBody')}
          </Alert>
        ) : null}

        {/* Only money that landed is "in My money"; a requested move says it waits for our team. */}
        {outcome.moved > 0 ? (
          <Alert tone="success" title={t('finance.gameWithdraw.doneTitle', { amount: fmt(outcome.moved) })} />
        ) : null}
        {outcome.requested > 0 ? (
          <Alert tone="info" title={t('finance.gameWithdraw.requestedTitle', { amount: fmt(outcome.requested) })}>
            {t('finance.gameWithdraw.requestedBody')}
          </Alert>
        ) : null}

        {showRows && items.some((item) => getProviderId(item) && (getBalance(item) > 0 || getWithdrawMax(item) > 0)) ? (
          <List inset>
            {items.map((item) => {
              const id = getProviderId(item);
              const inGame = getBalance(item);
              const winning = getWinningBalance(item);
              const maxWithdraw = getWithdrawMax(item);
              const riverPay = isRiverPayProviderCode(getCode(item));
              // Keep River Pay visible when in-game > 0 even if win is $0 (user sees why Max is low).
              if (!id || (inGame <= 0 && maxWithdraw <= 0)) return null;
              const amt = amounts[id] || 0;
              const name = getName(item);
              return (
                <div key={id} role="listitem" className={rowClass}>
                  <GameIcon code={getCode(item)} />
                  <div className="col-span-2 flex min-w-0 flex-col @bp-sm:col-span-1">
                    <span className="truncate font-ui text-body font-semibold text-primary" title={name}>
                      {name}
                    </span>
                    <span className="font-ui text-small text-tertiary">
                      {riverPay
                        ? t('finance.gameWithdraw.riverPayAvailable', {
                            inGame: fmt(inGame),
                            win: fmt(getDisplayIntegerDollars(winning)),
                          })
                        : t('finance.gameWithdraw.available', { amount: fmt(maxWithdraw) })}
                    </span>
                  </div>
                  <div
                    className="col-span-2 min-w-0 @bp-sm:col-span-1"
                    onFocus={() => {
                      setFocusedId(id);
                      setDrafts((prev) => ({
                        ...prev,
                        [id]: amt > 0 ? String(amt) : '',
                      }));
                    }}
                    onBlur={() => {
                      const raw = drafts[id] ?? '';
                      const n = raw === '' || raw === '.' ? 0 : parseFloat(raw);
                      setAmt(id, Number.isFinite(n) ? n : 0, maxWithdraw);
                      setFocusedId((cur) => (cur === id ? null : cur));
                      setDrafts((prev) => {
                        const next = { ...prev };
                        delete next[id];
                        return next;
                      });
                    }}
                  >
                    <AmountInput
                      aria-label={t('finance.gameWithdraw.amountLabel', { game: name })}
                      value={focusedId === id ? (drafts[id] ?? '') : amt > 0 ? amt.toFixed(2) : ''}
                      onChange={(next) => {
                        setDrafts((prev) => ({ ...prev, [id]: next }));
                        if (next === '' || next === '.') {
                          setAmt(id, 0, maxWithdraw);
                          return;
                        }
                        const n = parseFloat(next);
                        if (Number.isFinite(n)) setAmt(id, n, maxWithdraw);
                      }}
                    />
                  </div>
                  <Button
                    variant="secondary"
                    size="lg"
                    disabled={maxWithdraw <= 0}
                    onClick={() => {
                      setAmt(id, maxWithdraw, maxWithdraw);
                      if (focusedId === id) {
                        setDrafts((prev) => ({ ...prev, [id]: String(maxWithdraw) }));
                      }
                    }}
                  >
                    {t('finance.gameWithdraw.max')}
                  </Button>
                </div>
              );
            })}
          </List>
        ) : null}

        {showRows && items.every((i) => getWithdrawMax(i) <= 0 && getBalance(i) <= 0) ? (
          <p className="m-0 font-ui text-body text-secondary">{t('finance.gameWithdraw.empty')}</p>
        ) : null}
      </div>
    </Dialog>
  );
}
